import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { HotkeysProvider } from 'react-hotkeys-hook';
import { useFullScreenHandle } from 'react-full-screen';
import DownIcon from '@mui/icons-material/SouthEast';
import UpIcon from '@mui/icons-material/NorthWest';
import { Resizable } from 're-resizable';
import cx from 'classnames';

import { ClipFavouritesProvider } from '../ClipFavourites/context';
import { ClipFilterProvider, useClipFilter } from '../ClipFilter/context';
import { VideoDrawingProvider, useDrawingMarkers } from '../VideoDrawing/context';
import { setShowSpeedOption, updateCurrentVideoThatInFrame, updateSelectedVideoQuality } from '../../store/sharedStore/sharedStoreSlice';
import { getTooltipShown, getVideoQualityFromLocalStorage, setTooltipShown, setVideoQualityToLocalStorage, useDimensions } from '../../helpers';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import ComposerVideoPlayer from '../modules/ComposerVideoPlayer';
import { findSearchValue } from '../../helpers/getSearchValue';
import VideoComments, { SidebarToggle } from '../modules/VideoComments';
import styles from './ComposerVideoPage.module.scss';
import qualityMenuStyles from '../modules/VideoQualitySelector/VideoQualitySelector.module.scss';
import { updateCustomTime, updateComposerPlayControls } from '../../store/composers/composersSlice';
import { VIDEO_TYPE } from '../../enums';
import Composer from '../Composer';

export const speedOptions = [0.25, 0.5, 0.75, 1.0, 1.25, 1.5, 2.0];

const ComposerVideoPage: React.FC = () => {
  const fullScreen = useFullScreenHandle();
  const dispatch = useAppDispatch();

  const { currentComposerVideos, composerPlayControl, composerVideoClips, currentComposerData } = useAppSelector(
    (state) => state.composersStore,
  );
  const { showSidebar, showTooltipsForComposerResize, selectedVideoQuality, showSpeedOption } = useAppSelector(
    (state) => state.sharedStore,
  );

  const { widthD, heightD } = useDimensions();
  const { sidebarVisible: showCommentsSection, setSidebarVisible: setShowCommentSection } = useClipFilter();
  const lastWideCommentsState = useRef(true);
  const commentsAutoHidden = useRef(false);
  const switchCommentsView = useCallback(() => {
    setShowCommentSection(!showCommentsSection);
  }, [showCommentsSection]);

  const clipIndex = findSearchValue('clip') as unknown as number;
  const commentTabFromQuery = findSearchValue('commentTab');

  useEffect(() => {
    if (commentTabFromQuery === 'video') {
      setShowCommentSection(true);
    }
  }, [commentTabFromQuery]);

  // we need this for autozoom auto adjustments
  const [dynamicZoom, setDynamicZoom] = useState(false);

  useEffect(() => {
    if (clipIndex) {
      dispatch(updateCustomTime(composerPlayControl.composerControls.playedSeconds));
    }
  }, [clipIndex]);

  const [videoPlayerHeight, setVideoPlayerHeight] = useState(450);
  const { drawingMinHeight } = useDrawingMarkers();
  const [mobileVideoPlayerHeight, setMobileVideoPlayerHeight] = useState<number | null>(null);
  const manuallyResizedVideoRef = useRef(false);
  const mobileWorkspaceWidth = Math.max(0, widthD - 64);
  const desktopWorkspaceWidth = Math.max(0, widthD - (showSidebar ? 303 : 74) - (showCommentsSection ? 315 : 50));
  const maxVideoPlayerHeight = Math.max(140, drawingMinHeight, heightD - 62 - 120);
  const shortViewportVideoCap = Math.min(
    Math.max(180, heightD - 62 - 260),
    Math.max(180, Math.round((desktopWorkspaceWidth * 9) / 16)),
  );
  const resolvedVideoPlayerHeight = Math.max(drawingMinHeight,
    widthD < 768
      ? Math.min(mobileVideoPlayerHeight ?? Math.max(140, Math.round((mobileWorkspaceWidth * 9) / 16)), maxVideoPlayerHeight)
      : heightD < 900 && !manuallyResizedVideoRef.current
        ? Math.min(videoPlayerHeight, shortViewportVideoCap)
        : videoPlayerHeight);

  useEffect(() => {
    if (widthD >= 1024 && !commentsAutoHidden.current) {
      lastWideCommentsState.current = showCommentsSection;
    }
  }, [showCommentsSection, widthD]);

  useEffect(() => {
    if (widthD < 1024) {
      if (!commentsAutoHidden.current) {
        lastWideCommentsState.current = showCommentsSection;
        commentsAutoHidden.current = true;
      }
      setShowCommentSection(false);
    } else if (commentsAutoHidden.current) {
      commentsAutoHidden.current = false;
      setShowCommentSection(lastWideCommentsState.current);
    }
  }, [widthD]);

  const [showResizeTooltip, setShowResizeTooltip] = useState(false);

  useEffect(() => {
    const showToolTip = getTooltipShown();
    if (!showToolTip) {
      setShowResizeTooltip(true);
      setTimeout(() => {
        setShowResizeTooltip(false);
        setTooltipShown();
      }, 8000);
    } else if (showTooltipsForComposerResize) {
      setShowResizeTooltip(true);
    } else {
      setShowResizeTooltip(false);
    }
  }, [showTooltipsForComposerResize]);

  const [videoData, setVideoData] = useState<VIDEO_TYPE>();
  const [computedQuality, setComputedQuality] = useState('');
  const activeAngleVideos = composerVideoClips?.length ? composerVideoClips : currentComposerVideos;
  const activeAngleVideo = activeAngleVideos[clipIndex] || activeAngleVideos[0];

  const videoUrl = useMemo(() => {
    const localVideoData = composerVideoClips?.length
      ? composerVideoClips[clipIndex]
      : currentComposerVideos[clipIndex]
      ? currentComposerVideos[clipIndex]
      : currentComposerVideos[0];
    if (!localVideoData) return '';
    setVideoData(localVideoData);
    const quality = getVideoQualityFromLocalStorage();
    if (quality && typeof localVideoData?.s3_keys === 'object' && localVideoData.s3_keys[quality]) {
      setComputedQuality(quality);
      const url = localVideoData.s3_keys[quality];
      return url;
    } else if (
      localVideoData?.s3_keys &&
      typeof localVideoData.s3_keys === 'object' &&
      (localVideoData?.s3_keys['1080'] || localVideoData?.s3_keys['720'] || localVideoData?.s3_keys['480'])
    ) {
      const fallbackQuality = localVideoData.s3_keys['1080'] ? '1080' : localVideoData.s3_keys['720'] ? '720' : '480';
      setComputedQuality(fallbackQuality);
      const url = localVideoData.s3_keys['1080'] || localVideoData.s3_keys['720'] || localVideoData.s3_keys['480'];
      return url;
    } else if (selectedVideoQuality) {
      setComputedQuality(selectedVideoQuality.quality || '');
      const url = selectedVideoQuality.url;
      return url;
    }
  }, [
    composerVideoClips,
    currentComposerVideos,
    selectedVideoQuality,
    clipIndex,
    composerPlayControl.currentClipIndex,
    composerPlayControl.currentRowIndex,
  ]);

  const onVideoQualitySet = (videoQualityArr: [string, string]) => {
    // videoQualityArr: ['1080', 'www.video.com/video.mp4']
    dispatch(updateSelectedVideoQuality({ quality: videoQualityArr[0], url: videoQualityArr[1] }));
    setVideoQualityToLocalStorage(`${videoQualityArr[0]}`);
  };
  useEffect(() => {
    if (activeAngleVideo) {
      dispatch(updateCurrentVideoThatInFrame(activeAngleVideo));
    }
    const rememberedQuality = getVideoQualityFromLocalStorage(); // string like "1080"
    if (activeAngleVideo) {
      if (
        rememberedQuality?.length &&
        activeAngleVideo.s3_keys[rememberedQuality] &&
        activeAngleVideo.s3_keys[rememberedQuality]?.length
      ) {
        onVideoQualitySet([rememberedQuality, activeAngleVideo.s3_keys[rememberedQuality]]); //['1080', 'www.video.com/video.mp4']
      } else {
        let highestKey = null;
        let highestValue = null;
        for (const key in activeAngleVideo.s3_keys) {
          if ((!highestKey || parseInt(key) > parseInt(highestKey)) && activeAngleVideo.s3_keys[key].length) {
            highestKey = key;
            highestValue = activeAngleVideo.s3_keys[key];
          }
        }
        if (highestKey && highestValue) {
          onVideoQualitySet([highestKey, highestValue]);
        }
      }
    }
  }, [activeAngleVideo]);

  const [speedPos, setSpeedPos] = useState({ top: 0, left: 0 });

  const [showMobileFeedFlash, setShowMobileFeedFlash] = useState(false);
  const [mobileFeedFlashTitle, setMobileFeedFlashTitle] = useState('');
  const previousMobileFeedIdRef = useRef<string | null>(null);
  const mobileFeedFlashTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const feedId = findSearchValue('feedId');

  useEffect(() => {
    return () => {
      if (mobileFeedFlashTimeoutRef.current) {
        clearTimeout(mobileFeedFlashTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (!feedId) return;

    if (widthD >= 768) {
      previousMobileFeedIdRef.current = feedId;
      return;
    }

    if (previousMobileFeedIdRef.current === null) {
      previousMobileFeedIdRef.current = feedId;
      return;
    }

    if (
      previousMobileFeedIdRef.current !== feedId &&
      currentComposerData?.id === feedId &&
      typeof currentComposerData?.title === 'string' &&
      currentComposerData.title.trim().length
    ) {
      previousMobileFeedIdRef.current = feedId;
      setMobileFeedFlashTitle(currentComposerData.title);
      setShowMobileFeedFlash(true);

      if (mobileFeedFlashTimeoutRef.current) {
        clearTimeout(mobileFeedFlashTimeoutRef.current);
      }

      mobileFeedFlashTimeoutRef.current = setTimeout(() => {
        setShowMobileFeedFlash(false);
      }, 2000);
    }
  }, [feedId, currentComposerData?.id, currentComposerData?.title, widthD]);

  useEffect(() => {
    const speedOptionMenuRef = document.querySelector('#composerPlaybackRef');
    if (speedOptionMenuRef) {
      const rect = speedOptionMenuRef.getBoundingClientRect();
      setSpeedPos({ top: rect.top + 30, left: rect.left });
    }
  }, [showSpeedOption]);
  const selectSpeed = (e: React.MouseEvent<HTMLDivElement>) => {
    dispatch(
      updateComposerPlayControls({
        ...composerPlayControl.composerControls,
        playbackRate: Number(e.currentTarget.id),
      }),
    );
    setTimeout(() => {
      dispatch(setShowSpeedOption(false));
    }, 150);
  };

  const renderPlayback = useMemo(() => {
    return (
      <div
        className={qualityMenuStyles.playbackMenu}
        style={{
          color: 'white',
          position: 'absolute',
          zIndex: 9999,
          top: `${speedPos.top - 180}px`,
          left: `${speedPos.left}px`,
          display: showSpeedOption ? 'flex' : 'none',
        }}
      >
        {speedOptions.map((speed) => (
          <div
            key={speed}
            onClick={showSpeedOption ? selectSpeed : console.log}
            id={speed.toString()}
            className={cx(qualityMenuStyles.option, composerPlayControl.composerControls.playbackRate === speed && qualityMenuStyles.selectedOption)}
          >
            {speed === 1.0 ? 'Normal' : speed}
          </div>
        ))}
      </div>
    );
  }, [showSpeedOption, speedPos]);

  return (
    <div className={cx(styles.videoPage, showSidebar && styles.expandedSidebarOffset)}>
      {widthD < 768 && showMobileFeedFlash && (
        <div className={styles.mobileFeedFlash} role="status" aria-live="polite">
          <div className={styles.mobileFeedFlashLabel}>Opened feed</div>
          <div className={styles.mobileFeedFlashTitle}>{mobileFeedFlashTitle}</div>
        </div>
      )}
      <HotkeysProvider>
        <>
          <div
            className={cx(
              styles.videoPageContent,
              !showCommentsSection && styles.videoPageContentFullWidth,
              widthD < 768 && showCommentsSection && styles.mobileCommentsOpen,
            )}
          >
            <Resizable
              size={{ height: resolvedVideoPlayerHeight, width: 'auto' }}
              onResizeStart={() => {
                if (widthD >= 768) manuallyResizedVideoRef.current = true;
              }}
              onResizeStop={(e, direction, ref, d) => {
                if (widthD < 768) {
                  setMobileVideoPlayerHeight(Math.min(maxVideoPlayerHeight, Math.max(140, ref.offsetHeight)));
                } else {
                  setVideoPlayerHeight(ref.offsetHeight);
                }
              }}
              minHeight={Math.max(drawingMinHeight, widthD < 768 ? 140 : 0) || undefined}
              maxHeight={widthD < 768 ? maxVideoPlayerHeight : undefined}
              handleClasses={{
                right: 'pointer-events-none',
                topRight: 'pointer-events-none',
                bottomRight: 'pointer-events-none',
                bottomLeft: 'pointer-events-none',
                topLeft: 'pointer-events-none',
                left: 'pointer-events-none',
              }}
              handleComponent={{
                bottom: (
                  <div
                    data-composer-resize-control="true"
                    style={{
                      zIndex: 50,
                      position: 'absolute',
                      width: widthD < 768 ? '100%' : '100vw',
                      right: '0px',
                      top: widthD < 768 ? '-9px' : '0px',
                      height: widthD < 768 ? '28px' : '10px',
                      cursor: widthD < 768 ? 'row-resize' : undefined,
                      touchAction: widthD < 768 ? 'none' : undefined,
                    }}
                  />
                ),
              }}
            >
              <div className={styles.videoPlayer}>
                {showResizeTooltip && (
                  <div className={styles.tooltip}>
                    Drag this corner up or down to adjust video player size.
                    <div className={styles.downIconContainer}>
                      <UpIcon />
                    </div>
                  </div>
                )}
                <ComposerVideoPlayer
                  videoUrl={currentComposerData?.topics.find((t) => t.clips.length) ? videoUrl : ''}
                  clipsCount={composerVideoClips?.length ? composerVideoClips.length - 1 : currentComposerVideos.length - 1}
                  videoData={videoData}
                  showCommentsSection={showCommentsSection}
                  videoPlayerHeight={resolvedVideoPlayerHeight}
                  fullScreen={fullScreen}
                />
              </div>
            </Resizable>
            <div className={styles.videoMenu} style={{ height: `calc(100vh - ${resolvedVideoPlayerHeight}px - 62px)` }}>
              {showResizeTooltip && (
                <div className={styles.tooltip}>
                  Drag this corner up or down to adjust video player size.
                  <div className={styles.downIconContainer}>
                    <DownIcon />
                  </div>
                </div>
              )}
              <div
                className={styles.videoComposer}
                style={{
                  width:
                    widthD < 768
                      ? 'calc(100vw - 64px)'
                      : `calc(100vw - ${showSidebar ? '303px' : '74px'} - ${showCommentsSection ? '315px' : '50px'})`,
                }}
              >
                <Composer
                  key={findSearchValue('feedId')}
                  videoPlayerHeight={resolvedVideoPlayerHeight}
                  fullScreen={fullScreen}
                  videoUrl={currentComposerData?.topics.find((topic) => topic.clips.length) ? videoUrl : ''}
                  videoData={videoData}
                  setDynamicZoom={setDynamicZoom}
                  dynamicZoom={dynamicZoom}
                  computedQuality={computedQuality}
                  onShowComments={() => setShowCommentSection(true)}
                />
              </div>
            </div>
          </div>

          <div className={cx(styles.videoComments, !showCommentsSection && styles.hiddenComments)}>
            <div className={styles.videoCommentsPanel} aria-hidden={!showCommentsSection}>
              <VideoComments switchCommentsView={switchCommentsView} />
            </div>
            <div className={styles.videoCommentsHidden} aria-hidden={showCommentsSection}>
              <SidebarToggle expanded={false} onClick={switchCommentsView} />
            </div>
          </div>
        </>
      </HotkeysProvider>
      {renderPlayback}
    </div>
  );
};

const ComposerVideoPageWithFavourites = () => (
  <ClipFavouritesProvider>
    <ClipFilterProvider>
      <VideoDrawingProvider><ComposerVideoPage /></VideoDrawingProvider>
    </ClipFilterProvider>
  </ClipFavouritesProvider>
);
export default ComposerVideoPageWithFavourites;
